import { useEffect, useRef, useState } from 'react';
import Shepherd from 'shepherd.js';
import {
  tourTheme,
  createProgressFooter,
  TourStepConfig,
  TourSection
} from './tourSteps';
import { profileAPI } from '../../utils/api';
import { trackTourStarted, trackTourCompleted, trackTourDismissed } from '../../utils/analytics';

// Arrow indicator component styles
const arrowStyles = `
  @keyframes bounce-arrow {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }

  .tour-arrow-indicator {
    position: fixed;
    z-index: 10000;
    pointer-events: none;
    animation: bounce-arrow 1s ease-in-out infinite;
  }

  .tour-arrow-indicator svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

interface WalkthroughTourProps {
  section: TourSection;
  steps: TourStepConfig[];
  isActive: boolean;
  onComplete: () => void;
  onSkip?: () => void;
}

export default function WalkthroughTour({
  section,
  steps,
  isActive,
  onComplete,
  onSkip
}: WalkthroughTourProps) {
  const tourRef = useRef<Shepherd.Tour | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onSkipRef = useRef(onSkip);
  const renderCountRef = useRef(0);
  const [currentArrowElement, setCurrentArrowElement] = useState<HTMLDivElement | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const isMobile = window.innerWidth < 768;

  renderCountRef.current += 1;
  console.log(`[Tour] Component render #${renderCountRef.current} for section: ${section}`);

  // Keep refs up to date
  useEffect(() => {
    console.log('[Tour] onComplete or onSkip callback changed');
    onCompleteRef.current = onComplete;
    onSkipRef.current = onSkip;
  }, [onComplete, onSkip]);

  useEffect(() => {
    console.log(`[Tour] WalkthroughTour mounted for section: ${section}`);

    // Inject custom theme CSS
    if (!document.getElementById('shepherd-theme')) {
      const style = document.createElement('style');
      style.id = 'shepherd-theme';
      style.innerHTML = tourTheme;
      document.head.appendChild(style);
    }

    // Inject arrow styles
    if (!document.getElementById('tour-arrow-styles')) {
      const style = document.createElement('style');
      style.id = 'tour-arrow-styles';
      style.innerHTML = arrowStyles;
      document.head.appendChild(style);
    }

    const handleTourComplete = async () => {
      trackTourCompleted(section);
      try {
        console.log(`[Tour] Completing tour section: ${section}`);
        // Mark this section as completed in the backend
        await profileAPI.completeTourSection(section);
        console.log(`[Tour] Tour section ${section} marked complete in backend`);
        onCompleteRef.current();
      } catch (error) {
        console.error(`Failed to mark tour section ${section} as complete:`, error);
        // Still call onComplete even if API fails
        onCompleteRef.current();
      }
    };

    const handleTourCancel = async () => {
      trackTourDismissed(section);
      try {
        console.log('[Tour] User cancelled/skipped tour - disabling all tours');
        // User skipped - disable all future tours AND mark this section as completed
        // so it won't reappear after profile refresh
        await Promise.all([
          profileAPI.disableAllTours(),
          profileAPI.completeTourSection(section)
        ]);
        console.log(`[Tour] All tours disabled and section ${section} marked complete in backend`);
        onSkipRef.current?.();
      } catch (error) {
        console.error('Failed to disable tours:', error);
        onSkipRef.current?.();
      }
    };

    // Create tour instance
    if (!tourRef.current) {
      tourRef.current = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
          classes: 'shepherd-theme-custom',
          scrollTo: { behavior: 'smooth', block: 'center' },
          cancelIcon: {
            enabled: false
          }
        }
      });

      // Set up tour-level event listeners
      tourRef.current.on('complete', () => {
        handleTourComplete();
      });

      tourRef.current.on('cancel', () => {
        handleTourCancel();
      });
    }

    return () => {
      console.log(`[Tour] Cleanup #1 running for section: ${section}, tour active: ${tourRef.current?.isActive()}`);
      // Clean up tour when component unmounts
      if (tourRef.current && tourRef.current.isActive()) {
        
        // tourRef.current.cancel();
      }
    };
  }, [section]);

  useEffect(() => {
    console.log(`[Tour] Second useEffect - isActive: ${isActive}, steps.length: ${steps.length}, tourRef exists: ${!!tourRef.current}`);

    if (!isActive || !tourRef.current || steps.length === 0) return;

    const tour = tourRef.current;

    // Don't restart if tour is already active
    if (tour.isActive()) {
      console.log('[Tour] Tour is already active, not restarting');
      return;
    }

    console.log('[Tour] Starting tour setup...');

    // Clear existing steps
    tour.steps.forEach(step => tour.removeStep(step.id));

    // Helper function to create arrow indicator for mobile
    const createArrowIndicator = (
      targetSelector: string,
      placement: string = 'top',
      arrowOffset: { top?: number; left?: number } = {}
    ): HTMLDivElement | null => {
      const targetElement = document.querySelector(targetSelector);
      if (!targetElement) return null;

      console.log('[Tour] Arrow target element:', targetElement);
      if (targetElement instanceof HTMLElement) {
        Array.from(targetElement.attributes).forEach(attr => {
          console.log(`[Tour] targetElement attribute: ${attr.name}="${attr.value}"`);
        });
      }

      const arrow = document.createElement('div');
      arrow.className = 'tour-arrow-indicator';
      
      const rect = targetElement.getBoundingClientRect();
      
      // Determine arrow direction and position based on placement
      let arrowSvg = '';
      let top = 0;
      let left = 0;

      switch (placement) {
        case 'bottom':
          arrowSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="#80CD32"><path d="M12 4l-8 8h5v8h6v-8h5z"/></svg>';
          top = rect.bottom + 10;
          left = rect.left + rect.width / 2 - 24;
          break;
        case 'top':
          arrowSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="#80CD32"><path d="M12 20l8-8h-5V4H9v8H4z"/></svg>';
          top = rect.top - 58;
          left = rect.left + rect.width / 2 - 24;
          break;
        case 'left':
          arrowSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="#80CD32"><path d="M4 12l8-8v5h8v6h-8v5z"/></svg>';
          top = rect.top + rect.height / 2 - 24;
          left = rect.right + 10;
          break;
        case 'right':
          arrowSvg = '<svg width="48" height="48" viewBox="0 0 24 24" fill="#80CD32"><path d="M20 12l-8 8v-5H4V9h8V4z"/></svg>';
          top = rect.top + rect.height / 2 - 24;
          left = rect.left - 58;
          break;
      }

      // Apply arrow offset
      top += arrowOffset.top || 0;
      left += arrowOffset.left || 0;

      arrow.style.top = `${top}px`;
      arrow.style.left = `${left}px`;
      arrow.innerHTML = arrowSvg;
      
      console.log(`[Tour] Arrow positioned at top: ${top}px, left: ${left}px, placement: ${placement}`);
      
      document.body.appendChild(arrow);
      return arrow;
    };

    // Helper function to position modal near arrow on mobile
    const positionModalNearArrow = (
      targetSelector: string,
      placement: string = 'top',
      customWidth: number = 280,
      customSpacing: number = 60,
      offset: { top?: number; left?: number } = {},
      stepId: string = '',
      keepDefaults: { top?: boolean; left?: boolean; transform?: boolean } = {}
    ) => {
      const targetElement = document.querySelector(targetSelector);
      // Find the currently active modal by step ID to avoid selecting hidden modals from previous steps
      const modalElement = stepId 
        ? document.querySelector(`[data-shepherd-step-id="${stepId}"]`) as HTMLElement
        : document.querySelector('.shepherd-element.shepherd-enabled') as HTMLElement;
      
      if (!targetElement || !modalElement) {
        console.log(`[Tour] Modal positioning failed - target: ${!!targetElement}, modal: ${!!modalElement}, stepId: ${stepId}`);
        return;
      }
      
      console.log(`[Tour] Positioning modal for step "${stepId}" near ${targetSelector}`);

      const rect = targetElement.getBoundingClientRect();
      const modalWidth = customWidth; // Mobile modal width
      const modalHeight = modalElement.offsetHeight || 200; // Approximate height
      const padding = 20;

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'bottom':
          // Position below the element and arrow
          top = rect.bottom + customSpacing; // Arrow height + spacing
          left = Math.max(padding, Math.min(window.innerWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2));
          break;
        case 'top':
          // Position above the element and arrow
          top = Math.max(padding, rect.top - customSpacing - modalHeight);
          left = Math.max(padding, Math.min(window.innerWidth - modalWidth - padding, rect.left + rect.width / 2 - modalWidth / 2));
          break;
        case 'left':
          // Position to the right of element and arrow
          top = Math.max(padding, Math.min(window.innerHeight - modalHeight - padding, rect.top + rect.height / 2 - modalHeight / 2));
          left = Math.min(window.innerWidth - modalWidth - padding, rect.right + customSpacing);
          break;
        case 'right':
          // Position to the left of element and arrow
          top = Math.max(padding, Math.min(window.innerHeight - modalHeight - padding, rect.top + rect.height / 2 - modalHeight / 2));
          left = Math.max(padding, rect.left - customSpacing - modalWidth);
          break;
      }

      // Apply custom offsets if provided
      top += offset.top || 0;
      left += offset.left || 0;

      // Use setProperty with priority to override Shepherd's default centering
      const applyStyles = () => {
        console.log(`[Tour] Applying styles to "${stepId}": top=${top}px, left=${left}px, width=${modalWidth}px`);
        console.log(`[Tour] Keep defaults - top: ${keepDefaults.top}, left: ${keepDefaults.left}, transform: ${keepDefaults.transform}`);
        
        // Build CSS rules based on what we're allowed to override
        const cssRules: string[] = [];
        const inlineStyles: string[] = [];
        
        if (!keepDefaults.top) {
          cssRules.push(`top: ${top}px !important;`);
          modalElement.style.setProperty('top', `${top}px`, 'important');
        }
        
        if (!keepDefaults.left) {
          cssRules.push(`left: ${left}px !important;`);
          modalElement.style.setProperty('left', `${left}px`, 'important');
        }
        
        if (!keepDefaults.transform) {
          cssRules.push(`transform: none !important;`);
          modalElement.style.setProperty('transform', 'none', 'important');
        }
        
        // Always set max-width and position
        cssRules.push(`max-width: ${modalWidth}px !important;`);
        cssRules.push(`position: fixed !important;`);
        modalElement.style.setProperty('max-width', `${modalWidth}px`, 'important');
        modalElement.style.setProperty('position', 'fixed', 'important');
        
        // Method 2: Add inline style tag to override everything
        const styleId = `tour-modal-override-${stepId}`;
        let styleTag = document.getElementById(styleId) as HTMLStyleElement;
        if (!styleTag) {
          styleTag = document.createElement('style');
          styleTag.id = styleId;
          document.head.appendChild(styleTag);
        }
        styleTag.textContent = `
          [data-shepherd-step-id="${stepId}"] {
            ${cssRules.join('\n            ')}
          }
        `;
      };
      
      // Apply styles immediately
      applyStyles();
      
      // Re-apply after a short delay to override Shepherd's positioning
      setTimeout(() => {
        applyStyles();
        
        // Verify what actually got set
        const actualTop = window.getComputedStyle(modalElement).top;
        const actualLeft = window.getComputedStyle(modalElement).left;
        const actualTransform = window.getComputedStyle(modalElement).transform;
        console.log(`[Tour] Computed styles for "${stepId}": top=${actualTop}, left=${actualLeft}, transform=${actualTransform}`);
      }, 50);
      
      // Apply again after delay to ensure it sticks through any async operations
      setTimeout(() => {
        applyStyles();
      }, 150);
      
      // Clean up previous observer
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
      }
      
      // Watch for style changes and re-apply (in case re-renders reset them)
      mutationObserverRef.current = new MutationObserver((mutations) => {
        console.log(`[Tour] MutationObserver fired for "${stepId}" - ${mutations.length} mutations`);
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const currentTop = modalElement.style.getPropertyValue('top');
            const currentLeft = modalElement.style.getPropertyValue('left');
            
            console.log(`[Tour] Style changed for "${stepId}": top=${currentTop} (expected ${top}px), left=${currentLeft} (expected ${left}px)`);
            
            // Re-apply if wrong
            if (currentTop !== `${top}px` || currentLeft !== `${left}px`) {
              console.log(`[Tour] *** Styles were reset, re-applying! ***`);
              applyStyles();
            }
          }
        });
      });
      
      mutationObserverRef.current.observe(modalElement, {
        attributes: true,
        attributeFilter: ['style'],
        attributeOldValue: true
      });
      
      console.log(`[Tour] MutationObserver active for "${stepId}"`);
    };

    // Helper function to remove arrow indicator
    const removeArrowIndicator = (arrow: HTMLDivElement | null) => {
      if (arrow && arrow.parentNode) {
        arrow.parentNode.removeChild(arrow);
      }
    };

    // Filter steps based on device type (mobile vs desktop)
    const filteredSteps = steps.filter(step => {
      // If step is marked as mobileOnly, only include it on mobile devices
      if (step.mobileOnly) {
        return isMobile;
      }
      // Otherwise, include all steps
      return true;
    });

    // Add steps to tour
    filteredSteps.forEach((stepConfig, index) => {
      const originalWhen = stepConfig.when || {};
      
      const stepOptions: any = {
        id: stepConfig.id,
        title: stepConfig.title,
        text: stepConfig.text,
        buttons: stepConfig.buttons.map(button => ({
          ...button,
          text: `
            <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
              <span>${button.text}</span>
              ${index < filteredSteps.length - 1 && button.text.includes('Next')
                ? createProgressFooter(index + 1, filteredSteps.length)
                : ''
              }
            </div>
          `
        })),
        when: {
          ...originalWhen,
          show: function(this: any) {
            // Call original show handler if it exists
            if (originalWhen.show) {
              originalWhen.show.call(this);
            }
            
            // On mobile, create arrow indicator and position modal if element exists
            if (isMobile && stepConfig.attachTo) {
              const modalWidth = stepConfig.mobileConfig?.modalWidth ?? 280;
              const arrowSpacing = stepConfig.mobileConfig?.arrowSpacing ?? 60;
              const modalOffset = stepConfig.mobileConfig?.modalOffset ?? {};
              const arrowOffset = stepConfig.mobileConfig?.arrowOffset ?? {};
              const delay = stepConfig.mobileConfig?.delayPositioning ?? 100;
              const keepDefaults = {
                top: stepConfig.mobileConfig?.keepDefaultTop ?? false,
                left: stepConfig.mobileConfig?.keepDefaultLeft ?? false,
                transform: stepConfig.mobileConfig?.keepDefaultTransform ?? false
              };
              
              setTimeout(() => {
                const arrow = createArrowIndicator(
                  stepConfig.attachTo!.element,
                  stepConfig.attachTo!.on as string,
                  arrowOffset
                );
                setCurrentArrowElement(arrow);
                
                // Position modal near the arrow with custom parameters
                positionModalNearArrow(
                  stepConfig.attachTo!.element,
                  stepConfig.attachTo!.on as string,
                  modalWidth,
                  arrowSpacing,
                  modalOffset,
                  stepConfig.id,  // Pass step ID to find the correct modal
                  keepDefaults    // Pass keepDefaults flags
                );
              }, delay);
            }
          },
          hide: function(this: any) {
            // Remove arrow when hiding step
            if (isMobile) {
              removeArrowIndicator(currentArrowElement);
              setCurrentArrowElement(null);
              
              // Disconnect mutation observer
              if (mutationObserverRef.current) {
                mutationObserverRef.current.disconnect();
                mutationObserverRef.current = null;
              }
              
              // Remove injected style tag
              const styleId = `tour-modal-override-${stepConfig.id}`;
              const styleTag = document.getElementById(styleId);
              if (styleTag) {
                styleTag.remove();
              }
            }
            
            // Call original hide handler if it exists
            if (originalWhen.hide) {
              originalWhen.hide.call(this);
            }
          }
        }
      };

      // On desktop, attach to elements; on mobile, show modal only
      if (!isMobile && stepConfig.attachTo) {
        stepOptions.attachTo = stepConfig.attachTo;
      }

      if (stepConfig.advanceOn) {
        stepOptions.advanceOn = stepConfig.advanceOn;
      }

      tour.addStep(stepOptions);
    });

    console.log(`[Tour] Added ${steps.length} steps. Starting tour now...`);

    // Start the tour
    trackTourStarted(section);
    tour.start();

    console.log('[Tour] Tour started!');

    return () => {
      console.log(`[Tour] Cleanup #2 running for section: ${section}, tour active: ${tour.isActive()}`);
      // Clean up arrow indicator
      if (currentArrowElement && currentArrowElement.parentNode) {
        currentArrowElement.parentNode.removeChild(currentArrowElement);
      }
      // Clean up tour when dependencies change
      if (tour.isActive()) {
        // tour.cancel();
      }
    };
  }, [isActive, steps, section, currentArrowElement, isMobile]);

  // Cleanup arrow and observer on unmount
  useEffect(() => {
    return () => {
      if (currentArrowElement && currentArrowElement.parentNode) {
        currentArrowElement.parentNode.removeChild(currentArrowElement);
      }
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
    };
  }, [currentArrowElement]);

  return null; // This component doesn't render anything
}
